/*
 * ReWear Club — Cart drawer upsell ("Don't forget these" + bundle-aware).
 *
 * The bundle progress meter is rendered server-side in rwc-cart-upsell.liquid
 * and is always accurate. This script only populates the recommendation rail:
 *  - Bundle mode (shopper can still climb a tier): pulls from the configured
 *    bundle collection, filtered to BUNDLE-tagged, non-excluded, not-in-cart.
 *  - Otherwise: Shopify "related" recommendations for the last-added item.
 *
 * The container lives inside #CartDrawer, which re-renders server-side on every
 * cart change. We re-populate the rail by subscribing to PUB_SUB_EVENTS.cartUpdate.
 */
(function () {
  'use strict';

  // The cart page loads this script and so does the drawer, so on /cart the
  // tag appears twice and would otherwise run the whole module twice.
  if (window.__rwcUpsellLoaded) return;
  window.__rwcUpsellLoaded = true;

  var ROOT_SEL = '[data-rwc-upsell]';

  var SEED_KEY = 'rwcUpsellSeed';

  function money(cents) {
    var amount = Number(cents) / 100;
    try {
      var currency = window.Shopify && window.Shopify.currency && window.Shopify.currency.active;
      if (currency) {
        return new Intl.NumberFormat(document.documentElement.lang || 'en-GB', {
          style: 'currency',
          currency: currency
        }).format(amount);
      }
    } catch (e) { /* fall through to the plain format below */ }
    return '£' + amount.toFixed(2);
  }

  function escapeHtml(s) {
    return String(s == null ? '' : s).replace(/[&<>"]/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c];
    });
  }

  function parseIdList(str) {
    if (!str) return [];
    return str.split(',').map(function (s) { return parseInt(s.trim(), 10); }).filter(function (n) { return !isNaN(n); });
  }

  function parseVendors(str) {
    if (!str) return [];
    return str.split(',').map(function (s) { return s.trim().toLowerCase(); }).filter(Boolean);
  }

  function withWidth(url, w) {
    if (!url) return '';
    return url + (url.indexOf('?') > -1 ? '&' : '?') + 'width=' + w;
  }

  // Normalise a product from either JSON source into one shape.
  // source: 'reco'  -> /recommendations/products.json (prices in cents)
  //         'coll'  -> /collections/x/products.json    (prices as decimal strings)
  function normalize(p, source) {
    var variants = p.variants || [];
    var v = variants[0];
    if (!v) return null;

    var priceCents, compareCents, image, available;
    if (source === 'reco') {
      priceCents = v.price;
      compareCents = v.compare_at_price || 0;
      image = p.featured_image || (p.images && p.images[0]) || '';
      available = p.available !== false;
    } else {
      priceCents = Math.round(parseFloat(v.price) * 100);
      compareCents = v.compare_at_price ? Math.round(parseFloat(v.compare_at_price) * 100) : 0;
      image = (p.images && p.images[0] && (p.images[0].src || p.images[0])) || '';
      available = variants.some(function (x) { return x.available !== false; });
    }

    return {
      id: p.id,
      url: '/products/' + p.handle,
      title: p.title,
      vendor: p.vendor || '',
      image: image,
      priceCents: priceCents,
      compareCents: compareCents,
      multi: variants.length > 1,
      variantId: v.id,
      available: available,
      tags: (p.tags || []).map(function (t) { return String(t).toLowerCase().trim(); })
    };
  }

  function buildCard(n, bundleMode) {
    var imgTag = n.image
      ? '<img src="' + withWidth(n.image, 400) + '" alt="" loading="lazy" width="400" height="533">'
      : '';
    var onSale = n.compareCents && n.compareCents > n.priceCents;
    var priceHtml = onSale
      ? '<s>' + money(n.compareCents) + '</s>' + money(n.priceCents)
      : money(n.priceCents);
    var brand = n.vendor ? '<div class="rwc-uc__brand">' + escapeHtml(n.vendor) + '</div>' : '';
    var tag = n.countsToward ? '<span class="rwc-uc__tag">Counts +1</span>' : '';

    // Add control sits OUTSIDE the card anchor (no nested interactive elements).
    // A control says what it does: multi-variant products go to the page to
    // pick a size, single-variant ones add straight to the bag.
    var action = n.multi
      ? '<a class="rwc-uc__add" href="' + n.url + '" aria-label="Choose options for ' + escapeHtml(n.title) + '">Options</a>'
      : '<button type="button" class="rwc-uc__add" data-variant-id="' + n.variantId + '" data-product-id="' + n.id + '" aria-label="Add ' + escapeHtml(n.title) + ' to bag">Add</button>';

    return '<div class="rwc-uc">'
      + '<a class="rwc-uc__link" href="' + n.url + '">'
      + '<div class="rwc-uc__media">' + tag + imgTag + '</div>'
      + brand
      + '<p class="rwc-uc__name">' + escapeHtml(n.title) + '</p>'
      + '</a>'
      + '<div class="rwc-uc__foot"><span class="rwc-uc__price">' + priceHtml + '</span>' + action + '</div>'
      + '</div>';
  }

  function bindAdds(rail) {
    rail.querySelectorAll('button.rwc-uc__add').forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        var id = btn.getAttribute('data-variant-id');
        if (id) addVariant(id, btn.getAttribute('data-product-id'), btn);
      });
    });
  }

  function failCard(btn, message) {
    btn.removeAttribute('aria-busy');
    var card = btn.closest('.rwc-uc');
    if (!card) return;
    var note = card.querySelector('.rwc-uc__error');
    if (!note) {
      note = document.createElement('p');
      note.className = 'rwc-uc__error';
      note.setAttribute('role', 'status');
      card.appendChild(note);
    }
    note.textContent = message;
  }

  function addVariant(variantId, productId, btn) {
    // A root can live in the drawer or on the cart page. The drawer re-renders
    // itself in place; the cart page has no single call that refreshes items,
    // totals and the meter together, so it reloads rather than showing a
    // half-updated cart.
    var inDrawer = btn.closest('cart-drawer');
    var cart = inDrawer ? document.querySelector('cart-drawer') : null;
    btn.setAttribute('aria-busy', 'true');
    var card = btn.closest('.rwc-uc');
    var stale = card && card.querySelector('.rwc-uc__error');
    if (stale) stale.remove();

    var body = new FormData();
    body.append('id', variantId);
    body.append('quantity', 1);
    if (cart && cart.getSectionsToRender) {
      body.append('sections', cart.getSectionsToRender().map(function (s) { return s.id; }).join(','));
      body.append('sections_url', window.location.pathname);
    }

    var addUrl = (window.routes && window.routes.cart_add_url) || '/cart/add';
    fetch(addUrl, {
      method: 'POST',
      headers: { 'X-Requested-With': 'XMLHttpRequest', 'Accept': 'application/javascript' },
      body: body
    })
      .then(function (r) { return r.json(); })
      .then(function (res) {
        if (res.status) { // error response (e.g. sold out)
          failCard(btn, res.description || res.message || 'Could not add — try the product page.');
          return;
        }
        btn.removeAttribute('aria-busy');
        btn.classList.add('is-done');
        // Remember what was genuinely added last, so the related-products seed
        // reflects the shopper's most recent action rather than cart order.
        try { if (productId) sessionStorage.setItem(SEED_KEY, String(productId)); } catch (e) { /* private mode */ }
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'rwc-cart-upsell',
            productVariantId: String(variantId),
            cartData: res
          });
        }
        if (cart && cart.renderContents) {
          cart.renderContents(res); // refreshes line items, totals, and the server-rendered meter
          // Rail re-population happens via the cartUpdate subscriber below.
        } else {
          window.location.reload();
        }
      })
      .catch(function () { failCard(btn, 'Could not add — check your connection.'); });
  }

  function render(root, normalized, bundleMode) {
    var rail = root.querySelector('[data-rwc-upsell-rail]');
    var heading = root.querySelector('[data-rwc-upsell-heading]');
    root.dataset.rwcInit = 'done';

    if (!normalized.length) {
      root.hidden = true;
      return;
    }
    if (heading) {
      heading.textContent = bundleMode ? root.dataset.headingBundle : root.dataset.headingDefault;
    }
    rail.innerHTML = normalized.map(function (n) { return buildCard(n, bundleMode); }).join('');
    bindAdds(rail);
    root.hidden = false;
  }

  function initRoot(root) {
    if (root.dataset.rwcInit === 'pending') return;
    root.dataset.rwcInit = 'pending';

    var count = parseInt(root.dataset.count, 10) || 4;
    var bundleMode = root.dataset.bundleMode === 'true';
    var sourceCollection = root.dataset.sourceCollection || '';
    var bundleTag = (root.dataset.bundleTag || 'bundle').toLowerCase();
    var excluded = parseVendors(root.dataset.excludedVendors);
    var meterVisible = root.dataset.meter === 'true';
    var cartIds = parseIdList(root.dataset.cartIds);

    // Prefer the product the shopper most recently added. Shopify orders cart
    // lines by first add, not by recency, so cart.items.last is only a fallback
    // and is wrong whenever the last action bumped an existing line's quantity.
    var seedId = root.dataset.seedId;
    try {
      var remembered = parseInt(sessionStorage.getItem(SEED_KEY), 10);
      if (!isNaN(remembered) && cartIds.indexOf(remembered) > -1) seedId = String(remembered);
    } catch (e) { /* private mode: fall back to cart order */ }

    var url, source, filterFn;

    if (bundleMode && sourceCollection) {
      source = 'coll';
      url = '/collections/' + encodeURIComponent(sourceCollection) + '/products.json?limit=30';
      filterFn = function (n) {
        if (!n.available) return false;
        if (cartIds.indexOf(n.id) > -1) return false;
        if (excluded.indexOf(n.vendor.toLowerCase().trim()) > -1) return false;
        return n.tags.indexOf(bundleTag) > -1;
      };
    } else {
      source = 'reco';
      if (!seedId) { render(root, [], false); return; }
      url = '/recommendations/products.json?product_id=' + encodeURIComponent(seedId) +
            '&intent=related&limit=' + (count + cartIds.length + 2);
      filterFn = function (n) {
        if (!n.available) return false;
        if (cartIds.indexOf(n.id) > -1) return false;
        // While the meter is telling the shopper to add items to earn a
        // discount, never recommend a product that would not count.
        if (meterVisible && excluded.indexOf(n.vendor.toLowerCase().trim()) > -1) return false;
        return true;
      };
    }

    fetch(url, { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (data) {
        var normalized = (data.products || [])
          .map(function (p) { return normalize(p, source); })
          .filter(Boolean)
          .filter(filterFn);

        normalized.forEach(function (n) {
          n.countsToward = n.tags.indexOf(bundleTag) > -1 &&
            excluded.indexOf(n.vendor.toLowerCase().trim()) === -1;
        });

        // Surface the products that actually move the shopper up a tier.
        if (meterVisible) {
          normalized.sort(function (a, b) {
            return (b.countsToward ? 1 : 0) - (a.countsToward ? 1 : 0);
          });
        }

        render(root, normalized.slice(0, count), bundleMode);
      })
      .catch(function () { render(root, [], bundleMode); });
  }

  function initAll() {
    var roots = document.querySelectorAll(ROOT_SEL);
    for (var i = 0; i < roots.length; i++) initRoot(roots[i]);
  }

  function subscribeReinit() {
    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;
    subscribe(PUB_SUB_EVENTS.cartUpdate, function () {
      // #CartDrawer has been re-rendered server-side; repopulate once DOM settles.
      setTimeout(initAll, 80);
    });
  }

  function boot() {
    subscribeReinit();
    initAll();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
